import { useState, useRef, useEffect, type InputHTMLAttributes, type HTMLAttributes } from 'react';
import { FaIcon } from '@ifixit/ui/components/icon';
import { useTranslations } from '@ifixit/i18n/hooks';
import { useBreakpoint } from '@ifixit/ui/hooks/use-media-query';
import IFixitLogo from 'Shared/icons/logos/ifixit.svg';
import { Field, type FieldProps, Form, Formik, type FormikHelpers } from 'formik';
import { Recaptcha } from 'Shared/recaptcha';
import { recaptchaSiteKeyFor } from 'Shared/recaptcha-site-key';
import { Auth } from 'Shared/auth';
import { fireGlobal } from 'Shared/utils/global-events';
import { goToLastPage } from 'Shared/utils/last-page';
import { SSOLoginButtons } from '../SSOLoginButtons';
import { isAndroidMobileApp } from 'Shared/utils/mobile-app-helpers';
import { SentryError } from '@ifixit/sentry';
import { CodeInput } from '../Join/components/CodeInput';
import { cn } from '@ifixit/ui/lib/utils';
import initializeComponent from 'Shared/react-initialize';
import type { TranslationFunction } from '@ifixit/i18n/types';
import { setLastLoginMethod, getLastLoginMethod } from './lastLoginMethod';
import type { LoginMethod } from './lastLoginMethod';
import { RecentlyUsedBadge, RecentlyUsedLabel } from '../RecentlyUsedBadge';

const LogoContainer = ({ children, ...props }: HTMLAttributes<HTMLDivElement>) => (
   <div className="mx-auto max-md:hidden" {...props}>
      {children}
   </div>
);

/**
 * `sso_association` is the `host_name` AltHostLoginLib wrote into
 * `login_host_associations` (`Libs/Constants.php`). Each button hides itself
 * when its provider is switched off, so don't name one we aren't rendering.
 */
const SSO_PROVIDERS: Record<string, { name: string; isAvailable: () => boolean }> = {
   OAuth2_GOOGLE: { name: 'Google', isAvailable: () => Boolean(App.googleLoginEnabled) },
   facebook: { name: 'Facebook', isAvailable: () => Boolean(App.facebookLoginEnabled) },
   apple: {
      name: 'Apple',
      isAvailable: () => Boolean(App.appleLoginEnabled) && !isAndroidMobileApp(),
   },
};

function LogInPasswordInput(props: InputHTMLAttributes<HTMLInputElement>) {
   const [show, setShow] = useState(false);
   const handleClick = () => setShow(!show);

   return (
      <div className="flex items-center gap-2 relative">
         <input
            id="password"
            autoComplete="current-password"
            className="w-full min-h-10 rounded-lg"
            type={show ? 'text' : 'password'}
            {...props}
         />
         <button
            type="button"
            className="button-link size-10 absolute right-0 top-0 z-2 flex items-center justify-center"
            onClick={handleClick}
         >
            <FaIcon name={show ? 'eye-slash' : 'eye'} className="size-4 text-gray-400" />
         </button>
      </div>
   );
}

export const getErrorMessage = (response: any, t: TranslationFunction) => {
   switch (response.reason) {
      case 'email': {
         return App.loginMessages.invalid_email;
      }
      case 'password': {
         return App.loginMessages.invalid_password;
      }
      case 'invalid_code': {
         return App.loginMessages.invalid_code;
      }
      case 'banned': {
         return App.loginMessages.banned;
      }
      case 'captcha': {
         return App.loginMessages.captcha;
      }
      case 'tooManyFailures': {
         const expiration = Number.parseInt(response.lockedOut, 10);
         const xMessage =
            expiration == 1 ? t('waitOneMinuteToTryAgain') : t('waitALittleWhileBeforeTryingAgain');
         const message = App.loginMessages.tooManyFailures + ' ' + xMessage;

         return message.replace('%1', expiration.toString());
      }
      case 'tooManyCodeAttempts': {
         return App.loginMessages.tooManyCodeAttempts;
      }
      case 'tooManyCodeGenerations': {
         return App.loginMessages.tooManyCodeGenerations;
      }
      case 'tooManySuccesses': {
         const expiration = Number.parseInt(response.lockedOut, 10);
         const xMessage =
            expiration == 1 ? t('waitOneMinuteToTryAgain') : t('waitMinutesToTryAgain');
         const message = App.loginMessages.tooManySuccesses + ' ' + xMessage;
         return message.replace('%1', expiration.toString());
      }
      case 'disabledUser': {
         return App.loginMessages.disabledUser;
      }
      case 'insecurePassword': {
         return App.loginMessages.insecurePassword;
      }
      case 'facebook': {
         return t('pleaseLoginUsingFacebook');
      }
      case 'OAuth2_GOOGLE': {
         return t('pleaseLoginUsingGoogle');
      }
      case 'apple': {
         return t('pleaseLoginUsingApple');
      }
      case 'adminOnlyEntrypoint': {
         return App.loginMessages.adminOnlyEntrypoint;
      }
      default: {
         return response.reason;
      }
   }
};

export const LogIn = ({
   initialEmail,
   onLoginComplete,
   handleGoToJoin,
}: {
   initialEmail?: string;
   onLoginComplete?: () => void;
   handleGoToJoin?: () => void;
}) => {
   const t = useTranslations('Login');
   const [step, setStep] = useState<'email' | 'sso' | 'password' | 'code'>('email');
   const [userEmail, setUserEmail] = useState(initialEmail ?? '');
   const afterLogin = onLoginComplete ?? goToLastPage;
   const [codeSent, setCodeSent] = useState(false);
   const [codePending, setCodePending] = useState(false);
   const [ssoProvider, setSsoProvider] = useState<string | null>(null);
   const [apiError, setApiError] = useState<string | null>(null);

   const lastMethod = getLastLoginMethod();
   const emailRecentlyUsed = lastMethod === 'email' || lastMethod === 'loginWithCode';

   const loginContainerStyles = 'text-base min-md:w-[384px] max-w-full';
   const headingStyles = 'text-2xl font-medium text-center mt-0';

   // Only arm the captcha when the server enforces it for login; the key
   // alone may be present because the *register* flow needs it.
   const loginRecaptchaSiteKey = recaptchaSiteKeyFor('login');

   const recaptchaPlaceholderRef = useRef<HTMLDivElement>(null);
   const emailInputRef = useRef<HTMLInputElement>(null);
   const codeInputRef = useRef<HTMLInputElement>(null);
   const ssoHeadingRef = useRef<HTMLHeadingElement>(null);

   // Only autofocus the email input on larger screens. On mobile, opening the
   // keyboard pushes the SSO buttons up off the screen, so let the user tap in.
   // The SSO step has no input to land in, so focus its heading instead:
   // otherwise the email form unmounts under the focused submit button and
   // focus falls back to <body>, and a screen reader never hears the change.
   const isDesktop = useBreakpoint('md');
   useEffect(() => {
      if (isDesktop && step === 'email') {
         emailInputRef.current?.focus();
      } else if (step === 'sso') {
         ssoHeadingRef.current?.focus();
      }
   }, [isDesktop, step]);

   const handleSubmit = (
      values: { password: string },
      { setSubmitting }: FormikHelpers<{ password: string }>
   ) => {
      // Clear any previous errors
      setApiError(null);

      const loginHandler = (response: any) => {
         (Auth as any).clearCookies();

         if (response.loggedIn) {
            setLastLoginMethod('email');
            fireGlobal('login.success', response);
         } else {
            setApiError(getErrorMessage(response, t));
            fireGlobal('login.failure', response);
         }
      };

      const loginPromise = Recaptcha.executeInvisible(
         recaptchaPlaceholderRef.current,
         loginRecaptchaSiteKey,
         'login'
      )
         .then((recaptchaResponse: any) =>
            (Auth as any).sendLogin({
               login: userEmail,
               password: values.password,
               captchaResult: recaptchaResponse,
               ajax: true,
               handler: loginHandler,
            })
         )
         .then((result: any) => {
            setSubmitting(false);
            if (!result.loggedIn) {
               throw undefined;
            }
            afterLogin();
         });

      LoadingIndicator.withPromise(loginPromise, {
         pendingMessage: t('checkingYourEmailAndPassword'),
         successMessage: t('loginSuccessful'),
         failureMessage: t('loginUnsuccessful'),
      });
   };

   const handleEmailSubmit = async (
      values: { email: string },
      { setSubmitting }: FormikHelpers<{ email: string }>
   ) => {
      setUserEmail(values.email);
      // Clear any previous errors when moving to a new step
      setApiError(null);

      // Validate email against the API
      await (Auth as any).validateLogin(values.email, async (response: any) => {
         if (response.hasAccount) {
            // An account tied to a provider has no password of its own:
            // saveLoginAssociation() randomizes it. Offer that provider and a
            // code, rather than mailing a code nobody asked for.
            const provider = SSO_PROVIDERS[response.ssoAssociation];
            if (provider?.isAvailable()) {
               setSsoProvider(provider.name);
               setStep('sso');
            } else if (response.ssoAssociation) {
               // Nothing to choose between when the provider has no button
               // here, so send the code that is the only way in.
               await requestCode(values.email);
            } else {
               // Email exists and no SSO association, proceed to password step
               setStep('password');
            }
         } else {
            setApiError(getErrorMessage({ reason: 'email' }, t));
         }
      });

      setSubmitting(false);
   };

   const requestCode = async (email: string) => {
      // Clear any previous errors
      setApiError(null);

      const formValues = {
         login: email,
         captchaResult: null,
      };

      const loginCodeHandler = (response: any) => {
         if (response.emailSent) {
            setCodeSent(true);
            setStep('code');
         } else {
            setApiError(getErrorMessage(response, t));
         }
      };

      // Get captcha token if configured
      if (loginRecaptchaSiteKey) {
         const recaptchaResponse = await Recaptcha.executeInvisible(
            recaptchaPlaceholderRef.current,
            loginRecaptchaSiteKey,
            'login_code'
         );
         formValues.captchaResult = recaptchaResponse;
      }

      try {
         await (Auth as any).sendGetLoginCode(formValues, loginCodeHandler);
      } finally {
         setCodePending(false);
      }
   };

   const handleRequestCode = async () => {
      setCodePending(true);
      await requestCode(userEmail);
   };

   const handleCodeSubmit = (
      values: { code: string },
      { setSubmitting, resetForm }: FormikHelpers<any>
   ) => {
      // Clear any previous errors
      setApiError(null);

      const loginHandler = (response: any) => {
         (Auth as any).clearCookies();

         if (response.loggedIn) {
            setLastLoginMethod('loginWithCode');
            fireGlobal('login.success', response);
         } else {
            if (response.reason === 'password') {
               response.reason = 'invalid_code';
            }
            setApiError(getErrorMessage(response, t));
            // Clear the code field on failure so user can try again
            resetForm({ values: { code: '' } });
            // Refocus the code input so the user can retype right away
            setTimeout(() => {
               codeInputRef.current?.focus();
            }, 0);
            fireGlobal('login.failure', response);
         }
      };

      const loginPromise = (Auth as any)
         .sendLoginWithCode(
            {
               login: userEmail,
               code: values.code,
            },
            loginHandler
         )
         .then((result: any) => {
            setSubmitting(false);
            if (!result?.loggedIn) {
               throw new SentryError('Login failed');
            }
            afterLogin();
         });

      LoadingIndicator.withPromise(loginPromise, {
         pendingMessage: t('verifyingCode'),
         successMessage: t('loginSuccessful'),
         failureMessage: t('codeVerificationFailed'),
      });
   };

   const [ssoError, setSsoError] = useState<string | null>(null);
   const ssoLoginHandler = (response: any) => {
      (Auth as any).clearCookies();

      if (response.loggedIn) {
         const method = response.from as LoginMethod;
         if (method) {
            setLastLoginMethod(method);
         }
         fireGlobal('login.success', response);
         afterLogin();
      } else {
         setSsoError(getErrorMessage(response, t));
         fireGlobal('login.failure', response);
      }
   };

   if (step === 'email') {
      return (
         <div className={loginContainerStyles}>
            <div className="flex flex-col gap-6 mb-8">
               <LogoContainer>
                  <IFixitLogo />
               </LogoContainer>
               <h2 className={cn(headingStyles, { 'hidden': !App.showJoinLink })}>
                  {t('signInToYourAccount')}
               </h2>
            </div>
            {App.showJoinLink && (
               <div className="flex justify-center mb-4">
                  <span>{t('new')}</span>
                  &nbsp;
                  {handleGoToJoin ? (
                     <button onClick={handleGoToJoin} className="text-primary font-medium">
                        {t('joinUs')}
                     </button>
                  ) : (
                     <a href="/Join" className="text-primary font-medium">
                        {t('joinUs')}
                     </a>
                  )}
                  <span>.</span>
               </div>
            )}
            <div className="sign-up-wrapper flex flex-col gap-6 p-8 bg-card border-1 border-base rounded-base">
               <SSOLoginButtons
                  loginHandler={ssoLoginHandler}
                  error={ssoError}
                  setError={setSsoError}
               />
               <div className="sign-up-form w-full">
                  {apiError && (
                     <div
                        className="mb-4 text-red-500 text-center"
                        id="api-error"
                        data-testid="api-error"
                     >
                        {apiError}
                     </div>
                  )}
                  <Formik initialValues={{ email: userEmail }} onSubmit={handleEmailSubmit}>
                     {({ isSubmitting }) => (
                        <Form>
                           <div className="flex flex-col gap-3">
                              <Field name="email">
                                 {({ field }: FieldProps) => (
                                    <div>
                                       <label htmlFor="email" className="text-sm font-medium">
                                          {t('email')}
                                       </label>
                                       <input
                                          id="email"
                                          ref={emailInputRef}
                                          aria-label={t('email')}
                                          className="w-full min-h-10 rounded-lg mt-1"
                                          type="email"
                                          placeholder="albert@domain.com"
                                          autoComplete="username"
                                          required
                                          {...field}
                                          value={field.value || ''}
                                       />
                                    </div>
                                 )}
                              </Field>
                              <div>
                                 <button
                                    id="continueBtn"
                                    aria-label={t('signInWithEmail')}
                                    className="w-full button-primary button-lg z-1"
                                    type="submit"
                                    disabled={isSubmitting}
                                 >
                                    {t('signInWithEmail')}
                                 </button>
                                 {emailRecentlyUsed && <RecentlyUsedBadge />}
                              </div>
                           </div>
                        </Form>
                     )}
                  </Formik>
               </div>
            </div>
         </div>
      );
   }

   if (step === 'sso') {
      return (
         <div className={loginContainerStyles}>
            <div className="flex flex-col gap-6 mb-8">
               <LogoContainer>
                  <IFixitLogo />
               </LogoContainer>
               <h2 ref={ssoHeadingRef} tabIndex={-1} className={headingStyles}>
                  {t('signInToYourAccount')}
               </h2>
               <div className="text-center text-gray-600 dark:text-muted-foreground">
                  {userEmail}
                  <button
                     className="button-link text-primary font-medium ml-2"
                     onClick={() => setStep('email')}
                  >
                     ({t('change')})
                  </button>
               </div>
            </div>
            <div className="sign-up-wrapper flex flex-col gap-6 p-8 bg-card border-1 border-base rounded-base">
               {apiError && (
                  <div
                     className="mb-4 text-red-500 text-center"
                     id="api-error"
                     data-testid="api-error"
                  >
                     {apiError}
                  </div>
               )}
               <p
                  className="m-0 text-center text-gray-600 dark:text-muted-foreground"
                  data-testid="sso-association"
               >
                  {t('accountConnectedToProvider', { provider: ssoProvider ?? '' })}
               </p>
               <SSOLoginButtons
                  loginHandler={ssoLoginHandler}
                  error={ssoError}
                  setError={setSsoError}
               />
               {loginRecaptchaSiteKey && (
                  <div
                     ref={recaptchaPlaceholderRef}
                     className="showLogin js-captcha-container captcha-container"
                  />
               )}
               <div className="flex flex-col">
                  <button
                     id="requestCodeLink"
                     className="button-link text-primary font-medium"
                     onClick={handleRequestCode}
                     disabled={codePending}
                  >
                     {t('emailMeOneTimeLoginCode')}
                  </button>
                  {lastMethod == 'loginWithCode' && <RecentlyUsedLabel />}
               </div>
            </div>
         </div>
      );
   }

   if (step === 'code') {
      return (
         <div className={loginContainerStyles}>
            <div className="flex flex-col gap-6 mb-8">
               <LogoContainer>
                  <IFixitLogo />
               </LogoContainer>
               <h2 className={headingStyles}>{t('enterVerificationCode')}</h2>
               <div className="text-center text-gray-600 dark:text-muted-foreground">
                  {userEmail}
                  <button
                     className="button-link text-primary font-medium ml-2"
                     onClick={() => setStep('email')}
                  >
                     ({t('change')})
                  </button>
               </div>
               {codeSent && (
                  <div className="text-center text-gray-600 dark:text-muted-foreground">
                     {t('weSentVerificationCode')}
                  </div>
               )}
            </div>
            <div className="sign-up-wrapper flex flex-col gap-6 p-8 bg-card border-1 border-base rounded-base">
               <div className="sign-up-form w-full">
                  {apiError && (
                     <div
                        className="mb-4 text-red-500 text-center"
                        id="api-error"
                        data-testid="api-error"
                     >
                        {apiError}
                     </div>
                  )}
                  <Formik initialValues={{ code: '' }} onSubmit={handleCodeSubmit}>
                     <Form>
                        <div className="flex flex-col gap-6">
                           <Field name="code">
                              {({ field, form }: FieldProps) => (
                                 <CodeInput
                                    ref={codeInputRef}
                                    value={field.value || ''}
                                    onChange={async value => {
                                       const cleanValue = value.replace(/[^0-9]/g, '');
                                       await form.setFieldValue('code', cleanValue);
                                    }}
                                    inputId="login-code"
                                    autoFocus
                                 />
                              )}
                           </Field>
                           {loginRecaptchaSiteKey && (
                              <div
                                 ref={recaptchaPlaceholderRef}
                                 className="showLogin js-captcha-container captcha-container"
                              ></div>
                           )}
                           <button
                              id="verifyBtn"
                              className="w-full button-primary button-lg"
                              type="submit"
                           >
                              {t('signIn')}
                           </button>
                           <div className="text-center">
                              <button
                                 className="button-link text-primary font-medium"
                                 onClick={handleRequestCode}
                                 disabled={!codeSent}
                              >
                                 {t('sendNewCode')}
                              </button>
                           </div>
                        </div>
                     </Form>
                  </Formik>
               </div>
            </div>
         </div>
      );
   }

   return (
      <div className={loginContainerStyles}>
         <div className="flex flex-col gap-6 mb-8">
            <LogoContainer>
               <IFixitLogo />
            </LogoContainer>
            <h2 className={headingStyles}>{t('signInToYourAccount')}</h2>
            <div className="text-center text-gray-600 dark:text-muted-foreground">
               {userEmail}
               <button
                  className="button-link text-primary font-medium ml-2"
                  onClick={() => setStep('email')}
               >
                  ({t('change')})
               </button>
            </div>
         </div>
         <div className="sign-up-wrapper flex flex-col gap-6 p-8 bg-card border-1 border-base rounded-base">
            <div className="sign-up-form w-full">
               {apiError && (
                  <div
                     className="mb-4 text-red-500 text-center"
                     id="api-error"
                     data-testid="api-error"
                  >
                     {apiError}
                  </div>
               )}
               <Formik initialValues={{ password: '' }} onSubmit={handleSubmit}>
                  {({ isSubmitting }) => (
                     <Form>
                        <div className="flex flex-col gap-6">
                           {/* hidden username input to help password managers */}
                           <div className="hidden">
                              <input
                                 className="w-full min-h-10 rounded-lg"
                                 readOnly
                                 value={userEmail}
                                 autoComplete="username"
                              />
                           </div>
                           <Field name="password">
                              {({ field }: FieldProps) => (
                                 <div className="flex flex-col gap-2">
                                    <label htmlFor="password" className="text-sm font-medium">
                                       {t('password')}
                                    </label>
                                    <LogInPasswordInput
                                       aria-label={t('password')}
                                       autoFocus
                                       {...field}
                                       value={field.value || ''}
                                    />
                                 </div>
                              )}
                           </Field>
                           {loginRecaptchaSiteKey && (
                              <div
                                 ref={recaptchaPlaceholderRef}
                                 className="showLogin js-captcha-container captcha-container"
                              />
                           )}
                           <div>
                              <button
                                 id="loginBtn"
                                 aria-label={t('signInWithPassword')}
                                 className="w-full button-primary button-lg z-1"
                                 type="submit"
                                 disabled={isSubmitting}
                              >
                                 {t('signInWithPassword')}
                              </button>
                              {lastMethod == 'email' && <RecentlyUsedBadge />}
                           </div>
                           <div className="text-center text-muted-foreground my-4">{t('or')}</div>
                           <div className="flex flex-col">
                              <button
                                 id="requestCodeLink"
                                 className="button-link text-primary font-medium"
                                 onClick={handleRequestCode}
                                 disabled={isSubmitting || codePending}
                              >
                                 {t('emailMeOneTimeLoginCode')}
                              </button>
                              {lastMethod == 'loginWithCode' && <RecentlyUsedLabel />}
                           </div>
                        </div>
                     </Form>
                  )}
               </Formik>
            </div>
         </div>
      </div>
   );
};

initializeComponent('LogIn', LogIn);
